import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, MoreVertical, Star, TrendingUp, Eye, Edit, Trash2, MessageSquare } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { mockPropFirms } from '@/data/mock';
import { toast } from 'sonner';
import { deletePropFirms } from '@/services/propFirmApi';

export const PropFirmsPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedFirms, setSelectedFirms] = useState([]);
  const [firms, setFirms] = useState(mockPropFirms);
  const [deleteFirm, setDeleteFirm] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const filteredFirms = firms.filter((firm) => {
    const matchesSearch = firm.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || firm.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleToggleFeatured = (firmId, currentValue) => {
    setFirms(firms.map(f => 
      f.id === firmId ? { ...f, featured: !currentValue } : f
    ));
    toast.success(!currentValue ? 'Firm featured' : 'Firm unfeatured');
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedFirms(filteredFirms.map(f => f.id));
    } else {
      setSelectedFirms([]);
    }
  };

  const handleSelectFirm = (firmId, checked) => {
    if (checked) {
      setSelectedFirms([...selectedFirms, firmId]);
    } else {
      setSelectedFirms(selectedFirms.filter(id => id !== firmId));
    }
  };

  const handleBulkAction = (action) => {
    toast.success(`${action} applied to ${selectedFirms.length} firms`);
    setSelectedFirms([]);
  };

  return (
    <div>
      <PageHeader
        title="Prop Firms"
        description="Manage all prop firms in your directory"
        breadcrumbs={['Admin', 'Content', 'Prop Firms']}
        actions={
          <Button onClick={() => navigate('/dashboard/firms/new')}>
            <Plus className="h-4 w-4 mr-2" />
            Add Firm
          </Button>
        }
      />

      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardContent className="p-6">
          {/* Filters */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search firms..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="hidden">Hidden</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Bulk Actions Bar */}
          {selectedFirms.length > 0 && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
              <span className="text-sm font-medium text-blue-900">
                {selectedFirms.length} firm{selectedFirms.length > 1 ? 's' : ''} selected
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkAction('Activate')}
                >
                  Activate
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkAction('Draft')}
                >
                  Set as Draft
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBulkDeleteOpen(true)}
                  className="text-red-600 hover:text-red-700"
                >
                  Delete
                </Button>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedFirms.length === filteredFirms.length}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Firm
                  </TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Status
                  </TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Rating
                  </TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">
                    Starting Price
                  </TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">
                    Max Funding
                  </TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Reviews
                  </TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-center">
                    Featured
                  </TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredFirms.map((firm) => (
                  <TableRow key={firm.id} className="hover:bg-slate-50">
                    <TableCell>
                      <Checkbox
                        checked={selectedFirms.includes(firm.id)}
                        onCheckedChange={(checked) => handleSelectFirm(firm.id, checked)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <img
                          src={firm.logo}
                          alt={firm.name}
                          className="h-10 w-10 rounded"
                        />
                        <div>
                          <p className="text-sm font-medium text-slate-900">
                            {firm.name}
                          </p>
                          <p className="text-xs text-slate-500">{firm.country}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={firm.status} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                        <span className="text-sm font-medium text-slate-900">
                          {firm.rating}
                        </span>
                        <span className="text-xs text-slate-500">
                          ({firm.reviewCount})
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="text-sm font-medium text-slate-900">
                        ${firm.startingPrice}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="text-sm font-medium text-slate-900">
                        ${firm.maxFunding.toLocaleString()}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <MessageSquare className="h-3 w-3 text-blue-500" />
                        <span className="text-sm text-slate-600">
                          {firm.reviewCount}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={firm.featured}
                        onCheckedChange={() => handleToggleFeatured(firm.id, firm.featured)}
                      />
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/dashboard/firms/${firm.id}`)}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/dashboard/firms/${firm.id}/edit`)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => deletePropFirms(firm.id)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-slate-600">
              Showing <span className="font-medium">{filteredFirms.length}</span> of{' '}
              <span className="font-medium">{firms.length}</span> firms
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled>
                Previous
              </Button>
              <Button variant="outline" size="sm" disabled>
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!deleteFirm}
        onOpenChange={() => setDeleteFirm(null)}
        title="Delete Firm"
        description={`Are you sure you want to delete "${deleteFirm?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={() => {
          setFirms(firms.filter(f => f.id !== deleteFirm.id));
          toast.success('Firm deleted');
          setDeleteFirm(null);
        }}
      />

      <ConfirmDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        title="Delete Firms"
        description={`Are you sure you want to delete ${selectedFirms.length} firm${selectedFirms.length > 1 ? 's' : ''}? This action cannot be undone.`}
        confirmLabel="Delete All"
        variant="destructive"
        onConfirm={() => {
          setFirms(firms.filter(f => !selectedFirms.includes(f.id)));
          toast.success(`${selectedFirms.length} firms deleted`);
          setSelectedFirms([]);
          setBulkDeleteOpen(false);
        }}
      />
    </div>
  );
};
